import React, { useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { createItem, searchCandidates } from './api';

/** Find a catalogue item for one line (GET /api/review/candidates) or create a new one (POST /api/review/items). */
export default function LineMatchPanel({ lineView, onPick, onClose }) {
  const [q, setQ] = useState(lineView.raw);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const body = await searchCandidates(term);
        if (!cancelled) setResults(body?.candidates || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const create = async () => {
    const name = q.trim() || lineView.raw;
    setBusy(true);
    setError(null);
    try {
      const item = await createItem(name, lineView.line.unit || 'ea', lineView.skuTask?.document_id);
      await onPick(item.id, item.name || name);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const id = `line-search-${lineView.index}`;
  return (
    <div className="mt-3 rounded-xl border border-surface-border bg-surface-muted p-3 space-y-3" data-testid={`line-match-panel-${lineView.index}`}>
      <label htmlFor={id} className="text-xs uppercase tracking-overline text-ink-muted">Find the item</label>
      <div className="flex items-center gap-2 rounded-lg border border-surface-border bg-surface px-3">
        <Search className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
        <input id={id} value={q} onChange={(e) => setQ(e.target.value)} autoFocus
          data-testid={`line-search-input-${lineView.index}`}
          className="flex-1 min-h-[44px] bg-transparent outline-none text-ink" placeholder="Type an item name" />
      </div>
      <ul className="space-y-2">
        {results.map((c, k) => (
          <li key={`${c.item_id}-${k}`}>
            <button type="button" data-testid={`line-candidate-${lineView.index}-${c.item_id}`}
              onClick={() => onPick(c.item_id, c.name)}
              className="w-full text-left min-h-[48px] px-3 rounded-lg border border-surface-border bg-surface hover:border-primary-300 hover:bg-primary-50 flex items-center justify-between gap-2">
              <span className="text-ink">{c.name}</span>
              <span className="text-xs text-ink-subtle">{c.unit}</span>
            </button>
          </li>
        ))}
      </ul>
      {error && <p className="text-sm text-danger">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={create} disabled={busy} data-testid={`line-create-item-${lineView.index}`}
          className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg border border-primary-300 text-primary-700 hover:bg-primary-50 disabled:opacity-50">
          <Plus className="w-4 h-4" /> Create new item “{(q.trim() || lineView.raw).slice(0, 40)}”
        </button>
        <button type="button" onClick={onClose} data-testid={`line-match-close-${lineView.index}`}
          className="min-h-[44px] px-4 rounded-lg text-ink-muted hover:bg-surface-sunken">Close</button>
      </div>
    </div>
  );
}
